// Samford University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Samford's Common Data Set 2025-2026, published as one PDF per section on
//    the Institutional Analytics and Effectiveness Facts & Stats page (the
//    SOURCES url): Section C for C1 applicants, admits and enrollees for Fall
//    2025 with the residency table, and C8 test policy for Fall 2027 (SAT or
//    ACT "required for some", scores due May 1); Section G for G1/G5
//    2026-2027 costs. Quirk: C1's by-sex enrollee totals add to 1,156, while
//    the enrollees-by-status lines and the residency table give 1,157 (one
//    part-time student); 1,156 is used, as the brief's C1 rule says. C21 and
//    C22 (early decision and early action) are left blank in the CDS.
//  - Samford Admission: Apply to Samford University (freshman requirements,
//    testing, Fall 2027 test-optional choice), Apply Options (2026-27 test
//    optional process and who must still send scores), Early Action, FAQs
//    (rolling admission plan). Samford publishes no final application
//    deadline for rolling admission.
//  - Samford School of the Arts: Undergraduate Admission Process (dual
//    admission for music, theatre and dance), and the Music and Theatre and
//    Dance admission pages (audition weekends for 2026-2027).
//  - College Scorecard, school id 102049 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// No announced change for fall 2028 entry was found on the admissions pages.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Samford Common Data Set 2025-2026',
        url: 'https://www.samford.edu/departments/institutional-effectiveness/facts-and-stats',
        read: '2026-10-01',
    },
    apply: {
        label: 'Samford Admission, Apply to Samford University',
        url: 'https://www.samford.edu/admission/apply',
        read: '2026-10-01',
    },
    applyOptions: {
        label: 'Samford Admission, Apply Options',
        url: 'https://www.samford.edu/admission/undergraduate/apply-options',
        read: '2026-10-01',
    },
    earlyAction: {
        label: 'Samford Admission, Early Action',
        url: 'https://www.samford.edu/admission/early-action',
        read: '2026-10-01',
    },
    faqs: {
        label: 'Samford Admission, Frequently Asked Questions',
        url: 'https://www.samford.edu/admission/faqs',
        read: '2026-10-01',
    },
    arts: {
        label: 'Samford School of the Arts, Undergraduate Admission Process',
        url: 'https://www.samford.edu/arts/admission/undergraduate',
        read: '2026-10-01',
    },
    music: {
        label: 'Samford School of the Arts, Music Admission',
        url: 'https://www.samford.edu/arts/music/admission',
        read: '2026-10-01',
    },
    theatre: {
        label: 'Samford School of the Arts, Theatre and Dance Admission',
        url: 'https://www.samford.edu/arts/theatre-and-dance/admission',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?102049-Samford-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'samford-university',
    kbId: 'samford_university',
    name: 'Samford University',
    shortName: 'Samford',
    location: 'Birmingham, AL',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Samford brand guide); decoration only.
    branding: {
        primary: '#0C2340',
        secondary: '#BD1F2D',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/samford_university_logo.png',
    },

    faq: {
        answer: { text: 'Samford University\'s Early Action deadline for fall 2027 entry is November 1, and applicants who meet it are notified in early November.', source: 'faqs' },
        questions: [
            { q: 'What is the Samford University acceptance rate?', a: 'For fall 2025, Samford admitted 4,055 of 5,114 first-year applicants, 79.3%, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1' },
            { q: 'What is the Samford acceptance rate for out-of-state students?', a: 'For fall 2025, Samford admitted 3,405 of 3,563 out-of-state first-year applicants, 95.6%. For in-state applicants it was 44.1%, 596 of 1,353.', source: 'cds', where: 'section C1, residency table' },
            { q: 'Is Samford test optional?', a: 'Yes, for most applicants: you choose on the application whether to apply test optional. Nursing, University Fellows, 3+3 Fast Track Physical Therapy and 3+2 Fast Track Speech-Language Pathology applicants must still send SAT or ACT scores, as must students graduating high school early and students whose high school gives no numeric or letter grades.', source: 'applyOptions' },
            { q: 'Is Samford early action binding?', a: 'No. Early Action is non-binding, so you can apply to other colleges and have until May 1 to decide. If you miss November 1, you can still apply.', source: 'earlyAction' },
            { q: 'How much is Samford tuition?', a: 'Tuition for 2026-2027 is $42,590, plus $1,200 in required fees. On-campus housing is $8,130 and the meal plan $7,250.', source: 'cds', where: 'section G1' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 5114,
        admitted: 4055,
        enrolled: 1156,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 1353, admitted: 596 },
            { group: 'Out-of-state', applied: 3563, admitted: 3405 },
            { group: 'International', applied: 198, admitted: 54 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Samford\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Samford\'s Common Data Set leaves its early decision and early action sections blank, so it gives no early action acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Samford with your intended major. Some programs ask for more:',
        rows: [
            { school: 'Music, and Theatre and Dance', requirement: 'Admission to Samford and admission to the School of the Arts are two separate admissions: you apply to Samford, declare a School of the Arts major, and submit the division\'s own application.', source: 'arts' },
            { school: 'Music auditions', requirement: 'A music application, an artistic resume and headshot, and a required audition and interview. Materials are due ten days before your audition. Audition weekends: November 20-21, 2026, February 5-6, 2027 and February 19-20, 2027; other dates or a virtual audition by request.', source: 'music' },
            { school: 'Theatre and Dance auditions', requirement: 'An artistic resume and headshot, and a required audition or interview. Materials are due ten days before your audition. Audition weekends: November 20-21, 2026, February 5-6, 2027 and February 19-20, 2027; other dates or a virtual audition by request.', source: 'theatre' },
            { school: 'Nursing, University Fellows, 3+3 Fast Track Physical Therapy (Exercise Science), 3+2 Fast Track Speech-Language Pathology (Communication Sciences and Disorders)', requirement: 'SAT or ACT scores are required; these applicants cannot apply test-optional.' },
            { school: 'Early graduates and ungraded high schools', requirement: 'Students graduating high school early, and students whose high school gives no numeric or letter grades with a grading scale, must also send SAT or ACT scores.' },
        ],
        note: 'Samford says entrance requirements for certain majors and programs are being evaluated and asks you to check with your program.',
        source: 'applyOptions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Early Action', value: 'November 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Early November', binding: false },
            { plan: 'Rolling Admission', deadline: 'No published final deadline', decision: 'On a rolling basis after early action decisions, typically beginning in December', binding: false },
        ],
        note: 'The first scholarship deadline is December 1, with notification beginning mid-December; the second is February 15, and final awards come out in early March.',
        source: 'faqs',
        binding: 'Early action is not binding: you can apply to other colleges and have until May 1 to accept, even if admitted early. The application opens August 1, and Samford keeps accepting applications after the November 1 early action deadline.',
        bindingSource: 'earlyAction',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Students applying for fall 2027 choose on the application whether to send SAT or ACT scores. If you apply test-optional and have scores on file, they are not considered for admission or scholarships; changing your choice takes an appeal before your decision is released. Some applicants must still send scores; they are listed under program requirements. If you send scores, Samford superscores the ACT and SAT for admission and accepts the CLT.',
        source: 'apply',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some fall 2027 applicants, with scores due by May 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'applyOptions',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 42590 },
            { item: 'Required fees', amount: 1200 },
            { item: 'Housing (on campus)', amount: 8130 },
            { item: 'Food (on-campus meal plan)', amount: 7250 },
            { item: 'Books and supplies', amount: 1320 },
            { item: 'Transportation', amount: 1584 },
            { item: 'Other expenses', amount: 4286 },
        ],
        total: 66360,
        note: 'For a full-time undergraduate living on campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 32950,
            byIncome: [
                { income: '$0 to $30,000', amount: 25299 },
                { income: '$30,001 to $48,000', amount: 24217 },
                { income: '$48,001 to $75,000', amount: 27586 },
                { income: '$75,001 to $110,000', amount: 30393 },
                { income: 'Over $110,000', amount: 37166 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Samford\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
